"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Send, Loader as Loader2, ShieldCheck, Clock, Headphones } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const features = [{
  icon: ShieldCheck,
  title: "Regulated Compliance",
  desc: "GMP, GLP, and pharmacopeial compliant support."
}, {
  icon: Clock,
  title: "Rapid Technical Response",
  desc: "Direct consultation with qualified application specialists."
}, {
  icon: Headphones,
  title: "Lifecycle Support",
  desc: "Complete qualification, testing, and method transfer."
}];
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center rounded-xl bg-muted/40 border border-border">
        <div className="flex items-center justify-center size-14 rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="size-7" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Inquiry Submitted Successfully
        </h3>
        <p className="text-muted-foreground max-w-md mb-6 text-sm">
          {successMessage || "Thank you for reaching out. Our analytical instrumentation team will review your requirements and respond promptly."}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="cursor-pointer">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters required"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">
                Name <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">
                Work Email <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">
                Company Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">
              Message <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your testing needs, instrumentation models of interest, or application requirements..." className="bg-background min-h-32 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3.5 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium shadow-sm transition-all">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-4 mr-2" />
            Submitting Inquiry...
          </> : <>
            <Send className="size-4 mr-2" />
            Send Inquiry
          </>}
      </Button>
    </Form>;
}
export default function Inquiry() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="inquiryform" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {} : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-3">
              Application Support
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              Contact Us - Antech Solutions
            </h2>
            <p className="mt-3 text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
              Discuss your instrumentation, qualification, or testing requirements
              with our analytical application specialists.
            </p>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {} : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            <Card className="border border-border shadow-md bg-card rounded-2xl overflow-hidden">
              <CardHeader className="border-b border-border bg-muted/20 px-6 sm:px-8 py-5">
                <CardTitle className="text-lg font-semibold text-card-foreground">
                  Application & Instrumentation Inquiry
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Fill in your project details below to receive expert technical guidance.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 sm:p-8">
                <FormProvider formId="contact_form" sectionName="inquiry-form" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Inquiry" successMessage="Thank you! We have received your inquiry and will be in touch shortly.">
                  <InquiryFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {} : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12 pt-8 border-t border-border">
            {features.map((item, index) => {
            const Icon = item.icon;
            return <div key={index} data-index={index} className="flex flex-col items-center sm:items-start text-center sm:text-left">
                  <div className="flex items-center justify-center size-10 rounded-lg bg-secondary text-secondary-foreground mb-3">
                    <Icon className="size-5 text-primary" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">
                    {item.title}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}